import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";
import { IMAGES } from "../lib/site";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const { setOpen } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "gallery", label: t.nav.gallery },
    { id: "reviews", label: t.nav.reviews },
    { id: "products", label: t.nav.products },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <img
            src={IMAGES.logo}
            alt="TTcuts Barbershop"
            className="h-14 w-14 md:h-12 md:w-12 rounded-full object-cover transition-transform group-hover:scale-105"
          />
          <span className="font-display text-xl tracking-tight uppercase hidden sm:inline">
            TT<span className="text-gold">cuts</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((n) => (
            <a
              key={n.id}
              href={`#${n.id}`}
              data-testid={`nav-${n.id}`}
              className="label-eyebrow text-white/70 hover:text-gold transition-colors"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden md:flex items-center border border-white/10 rounded-sm overflow-hidden text-xs"
            data-testid="lang-switch"
          >
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1.5 label-eyebrow transition-colors ${
                lang === "en"
                  ? "bg-gold text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang("fi")}
              data-testid="lang-fi"
              className={`px-3 py-1.5 label-eyebrow transition-colors ${
                lang === "fi"
                  ? "bg-gold text-black"
                  : "text-white/60 hover:text-white"
              }`}
            >
              FI
            </button>
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid="header-book-btn"
            className="hidden md:inline-flex items-center gap-2 bg-gold text-black px-5 py-2.5 rounded-sm label-eyebrow transition-all hover:scale-[1.03] glow-gold-hover"
          >
            {t.nav.book}
          </button>

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setMobileOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {nav.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${n.id}`}
                  className="label-eyebrow text-white/80 hover:text-gold"
                >
                  {n.label}
                </a>
              ))}
              <div
                className="flex items-center border border-white/10 rounded-sm overflow-hidden text-xs w-fit"
                data-testid="mobile-lang-switch"
              >
                <button
                  onClick={() => setLang("en")}
                  className={`px-4 py-2 label-eyebrow ${
                    lang === "en" ? "bg-gold text-black" : "text-white/60"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("fi")}
                  className={`px-4 py-2 label-eyebrow ${
                    lang === "fi" ? "bg-gold text-black" : "text-white/60"
                  }`}
                >
                  FI
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
